import { clampClipFps } from '~/lib/utils';
import type { Clip } from '~/types/pipeline';
import type { BoxRef } from '~/types/selection';

function normalizeCells(cells: readonly BoxRef[], rowCount: number, colCount: number): BoxRef[] {
  const seen = new Set<string>();
  const out: BoxRef[] = [];
  for (const c of cells) {
    if (!Number.isInteger(c.row) || !Number.isInteger(c.col)) continue;
    if (c.row < 0 || c.row >= rowCount) continue;
    if (c.col < 0 || c.col >= colCount) continue;
    const key = `${c.row},${c.col}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ row: c.row, col: c.col });
  }
  return out;
}

function normalizeBlobs(blobs: readonly number[], blobCount: number): number[] {
  const seen = new Set<number>();
  const out: number[] = [];
  for (const i of blobs) {
    if (!Number.isInteger(i)) continue;
    if (i < 0 || i >= blobCount) continue;
    if (seen.has(i)) continue;
    seen.add(i);
    out.push(i);
  }
  return out;
}

/**
 * Drop out-of-range cells from grid clips and out-of-range blob indices from
 * pin clips. Clips of the inactive kind for the given mode are left untouched.
 * Clips that become empty are removed. No auto-fill — users create clips
 * explicitly from the canvas selection.
 */
export function reconcileClipsForGrid(
  existing: readonly Clip[],
  rowCount: number,
  colCount: number,
): Clip[] {
  if (rowCount <= 0 || colCount <= 0) {
    return existing.filter((clip) => clip.source.kind !== 'cells');
  }
  const out: Clip[] = [];
  for (const clip of existing) {
    if (clip.source.kind === 'cells') {
      const cells = normalizeCells(clip.source.cells, rowCount, colCount);
      if (cells.length === 0) continue;
      out.push({
        ...clip,
        source: { kind: 'cells', cells },
        fps: clampClipFps(clip.fps),
      });
    } else {
      out.push({ ...clip, fps: clampClipFps(clip.fps) });
    }
  }
  return out;
}

export function reconcileClipsForBlobs(existing: readonly Clip[], blobCount: number): Clip[] {
  if (blobCount <= 0) {
    return existing.filter((clip) => clip.source.kind !== 'blobs');
  }
  const out: Clip[] = [];
  for (const clip of existing) {
    if (clip.source.kind === 'blobs') {
      const blobs = normalizeBlobs(clip.source.blobs, blobCount);
      if (blobs.length === 0) continue;
      out.push({
        ...clip,
        source: { kind: 'blobs', blobs },
        fps: clampClipFps(clip.fps),
      });
    } else {
      out.push({ ...clip, fps: clampClipFps(clip.fps) });
    }
  }
  return out;
}
